import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { BUDGET_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', approved: 'Approved', closed: 'Closed' };

export function BudgetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const budgetId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.budgets.approve');

  const budgetQuery = useQuery({ queryKey: ['budget', budgetId], queryFn: () => financeApi.getBudget(budgetId) });
  const docsQuery = useQuery({ queryKey: ['budget-documents', budgetId], queryFn: () => genericDocumentApi.listFor('budget', budgetId) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
  const approveMutation = useMutation({ mutationFn: () => financeApi.approveBudget(budgetId), onSuccess: () => { showSuccess('Budget approved.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const closeMutation = useMutation({ mutationFn: () => financeApi.closeBudget(budgetId), onSuccess: () => { showSuccess('Budget closed.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  if (!budgetQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const { data: b, lines } = budgetQuery.data;

  return (
    <div className="page">
      <Link to="/finance/budgets">&larr; Back to Budgets</Link>
      <RecordHeader
        title={`Budget - ${b.budget_reference}`}
        entityType="budget"
        entityId={budgetId}
        fields={[
          { label: 'Project', value: b.project_name ?? '-' },
          { label: 'Fiscal year', value: b.year_label ?? '-' },
          { label: 'Total', value: `${b.currency_code} ${Number(b.total_amount).toLocaleString()}` },
          { label: 'Status', value: <StatusBadge status={b.status} label={STATUS_LABELS[b.status]} /> },
        ]}
        actions={
          <span className="row-actions">
            {b.status === 'draft' && <button className="btn-primary" onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve</button>}
            {b.status === 'approved' && <button className="btn-secondary" onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}>Close</button>}
          </span>
        }
        readOnly={!canManage}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  {lines && lines.length > 0 && (
                    <>
                      <h3>Budget Lines</h3>
                      <DataTable
                        rows={lines}
                        rowKey={(l) => l.id}
                        emptyMessage="No lines."
                        columns={[
                          { header: 'Account', render: (l) => `${l.account_code} - ${l.account_name}` },
                          { header: 'Description', render: (l) => l.description ?? '-' },
                          { header: 'Budgeted Amount', render: (l) => Number(l.budgeted_amount).toLocaleString() },
                        ]}
                      />
                    </>
                  )}
                </section>
                <CommentsThread entityType="budget" entityId={budgetId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('budget', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('budget', budgetId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('budget', documentId)}
                queryKeyToInvalidate={['budget-documents', budgetId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="budget" entityId={budgetId} actionStageMap={BUDGET_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
